/// <amd-module name="Core/Medius.Core.Web/Scripts/components/sendEmailDialog/sendEmailDialogForm"/>
import * as React from "react";
import { Button, ButtonGroup, Checkbox, DialogHeaderRenderProps, DialogPart, FlexContent, FlexLayout, FlexSpacer, ResponsiveGridLayout, TextField } from "@medius/ui-controls";
import { translate, getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { Contact, Recipient, SendEmailDialogFormFields } from "./types";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { EmailAddressMultiSelectInput } from "./emailAddressMultiSelectInput";
import { isNullOrEmpty, validateEmails } from "./utils";
import { ErrorItem } from "./errorItem";
import { RichTextInput } from "./richTextInput/richTextInput";

export interface SendEmailDialogFormProps {
    addressBook: Contact[];
    recipients: Recipient[];
    ccs: Recipient[];
    sendMeACopy: boolean;
    subject: string;
    body: string;
    headerProps: DialogHeaderRenderProps;
    onSendAction: (fields: SendEmailDialogFormFields) => void;
    onRequestClose: () => void;
}

export const SendEmailDialogForm = (props: SendEmailDialogFormProps) => {
    const [recipients, setRecipients] = React.useState<Recipient[]>(props.recipients);
    const [ccs, setCcs] = React.useState<Recipient[]>(props.ccs);
    const [sendMeACopy, setSendMeACopy] = React.useState<boolean>(props.sendMeACopy);
    const [subject, setSubject] = React.useState<string>(props.subject);
    const [body, setBody] = React.useState<string>(props.body);

    const errorMessages = React.useMemo(() => 
            recipients
        .concat(ccs)
        .filter(r => r.errorMessage !== undefined)
        .map(r => getLabelTranslation("#Core/errorEmailDeliveryFailed", r.emailAddress))
    , [recipients, ccs]);

    const onRecipientsChange = (event: MultiSelectChangeEvent) => {
        const values = event.target.value;
        const normalizedValues = values.map(v => v.emailAddress === undefined 
            ? {displayName: v.displayName, emailAddress: v.displayName}
            : v);

        setRecipients([...normalizedValues]);
    };
    
    const onCcsChange = (event: MultiSelectChangeEvent) => {
        const values = event.target.value;
        const normalizedValues = values.map(v => v.emailAddress === undefined 
            ? {displayName: v.displayName, emailAddress: v.displayName}
            : v);

        setCcs([...normalizedValues]);
    };
        
    return (
        <>
            <DialogPart.Title {...props.headerProps} />
            <DialogPart.Content width={"large"}>
                <ResponsiveGridLayout 
                    maxColumns={1}
                    columnWidth={7}
                    itemRowSpacing="medium"
                >
                    <fieldset>
                        <EmailAddressMultiSelectInput
                            inputLabel={translate("#Core/emailTo")}
                            isRequired={true}
                            addressBook={props.addressBook}
                            onChange={onRecipientsChange}
                            recipients={recipients}
                            className="send-email-to-recipients-input"
                        />
                        <EmailAddressMultiSelectInput
                            inputLabel={translate("#Core/emailCc")}
                            addressBook={props.addressBook}
                            onChange={onCcsChange}
                            recipients={ccs}
                            className="send-email-cc-recipients-input"
                        />
                        {!isNullOrEmpty(errorMessages) && errorMessages.map((message, index) => <ErrorItem key={index} errorMessage={message} />)} 
                    </fieldset>
                    <FlexLayout spacerMode="on" spacerSize="small" width="content">
                        <Checkbox
                            label={translate("#Core/emailSendMeCopy")}
                            onChange={(e) => setSendMeACopy(e.target.value)}
                            value={sendMeACopy}
                            data-testid="send-email-copy-send-me-copy"
                        />
                    </FlexLayout>
                    <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                        <FlexContent>
                            <TextField
                                isRequired
                                fieldLabel={translate("#Core/emailMessageSubject")}
                                errorMessages={!subject ? getLabelTranslation("#Core/formFieldRequiredErrorMessage", "Subject field") : []}
                                inputProps={{
                                    onChange:(e) => setSubject(e.target.value),
                                    value: subject,
                                    height: "medium",
                                }}
                                data-testid="send-email-dialog-subject"
                            />
                        </FlexContent>
                    </FlexLayout>
                    <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                        <RichTextInput
                            inputTestId='send-email-dialog-body'
                            value={body}
                            onChange={(value) => setBody(value)}
                        />
                    </FlexLayout>
                </ResponsiveGridLayout>
            </DialogPart.Content>
            <DialogPart.Footer>
                <FlexLayout>
                    <FlexSpacer />
                    <ButtonGroup>
                        <Button
                            data-testid="send-email-cancel-button"
                            label={translate("#Core/cancel")}
                            variant="secondary"
                            onClick={props.onRequestClose}
                        />
                        <Button
                            data-testid="send-email-send-button"
                            label={translate("#Core/sendEmail")}
                            onClick={() => props.onSendAction({
                                recipients,
                                ccs,
                                sendMeACopy,
                                subject,
                                body,
                            })}
                            disabled={isNullOrEmpty(recipients) || !validateEmails(recipients) || !validateEmails(ccs) || !subject || !body}
                        />
                    </ButtonGroup>
                </FlexLayout>
            </DialogPart.Footer>
        </>
    );
};